import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createException,
  getExceptions,
  getExceptionsCount,
  updateException,
} from "../../../api/homeSiteExceptions/homeSiteExceptions";
import { Exception, Exceptions } from "../../../api/homeSiteExceptions/types";
import React from "react";

export const useExceptionsData = (divcode: string) => {
  return useQuery<Exceptions>({
    queryKey: ["homeSiteExceptions", divcode],
    queryFn: () => getExceptions({ filter: `divisionCode=${divcode},docStatus=true` }),
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    refetchInterval: 1000 * 60 * 15, // Refetch every 15 minutes
    refetchIntervalInBackground: true,
    enabled: !!divcode,
  });
};

export const useExceptionsDataByHomesiteId = (divcode: string,homesiteId: string) => {
  return useQuery<Exceptions>({
    queryKey: ["exceptionsByHomesite", divcode, homesiteId],
    queryFn: () =>
      getExceptions({
        filter: `divisionCode=${divcode},homeSiteId=${homesiteId},docStatus=true`,
        order: "DESC",
        orderBy: "createdOnDate",
      }),
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    refetchInterval: 1000 * 60 * 15, // 15 minutes
    refetchIntervalInBackground: true,
    enabled: !!divcode,
  });
};

export const useExceptionsCountsData = (divcode: string) => {
  return useQuery<any>({
    queryKey: ["homeSiteExceptionCounts", divcode],
    queryFn: () =>
      getExceptionsCount({
        filter: `divisionCode=${divcode},status=true,docStatus=true`,
        fields: "homeSiteId",
        groupBy: "homeSiteId",
      }),
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    refetchInterval: 1000 * 60 * 15, // 15 minutes
    refetchIntervalInBackground: true,
    enabled: !!divcode,
  });
};

export const useExceptionsIncrementalData = (divCode: string, continuationToken: string) => {
  const queryClient = useQueryClient();
  React.useEffect(() => {
    if (!divCode || !continuationToken) return;
    const prefetch = async (initialContinuationToken: string) => {
      let token = initialContinuationToken;
      while (token) {
        const currentToken = token;
        const newContent = await queryClient.fetchQuery<Exceptions>({
          queryKey: ["homeSiteExceptions", divCode, currentToken],
          queryFn: () =>
            getExceptions({
              filter: `divisionCode=${divCode},docStatus=true`,
              continuationToken: currentToken,
            }),
        });
        queryClient.setQueryData<Exceptions>(
          ["homeSiteExceptions", divCode],
          (oldData) => {
            if (!oldData) return newContent;
            return {
              ...newContent,
              data: [...oldData.data, ...newContent.data],
            };
          }
        );
        token = newContent.continuationToken ?? "";
      }
    };

    prefetch(continuationToken);
  }, [continuationToken, divCode, queryClient]);
};

export const useCreateExceptions = (divCode: string,homesiteId: string): UseMutationResult<Exception, unknown, Partial<Exception>, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createException,
    onSuccess: (newException: Exception) => {
      queryClient.setQueryData(
        ["exceptionsByHomesite", divCode, homesiteId],
        (currentExceptions: Exceptions | undefined) => {
          if (!currentExceptions) return;
          return {
            ...currentExceptions,
            data: [newException, ...currentExceptions.data],
          };
        }
      );
      queryClient.setQueryData(
        ["homeSiteExceptions", divCode],
        (currentExceptions: Exceptions | undefined) => {
          if (!currentExceptions) return;
          return {
            ...currentExceptions,
            data: [newException, ...currentExceptions.data],
          };
        }
      );
    },
    onError: (error) => {
      console.error("Error creating exception:", error);
    },
  });
};

export const useUpdateExceptions = (divCode: string,homesiteId: string): UseMutationResult<Exception, unknown, Partial<Exception>, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateException,
    onSuccess: (updatedException: Exception) => {
      queryClient.setQueryData(
        ["exceptionsByHomesite", divCode, homesiteId],
        (currentExceptions: Exceptions | undefined) => {
          if (!currentExceptions) return;
          return {
            ...currentExceptions,
            data: currentExceptions?.data?.map((exception: Exception) => {
              const updatedExceptions =
                exception.id === updatedException.id
                  ? updatedException
                  : exception;
              return updatedExceptions;
            }),
          };
        }
      );
      queryClient.setQueryData(
        ["homeSiteExceptions", divCode],
        (oldData: Exceptions) => {
          if (!oldData) return;
          const updatedData = oldData?.data.map((exception: any) =>
            exception.id === updatedException.id ? updatedException : exception
          );
          const returnData = { ...oldData, data: updatedData };
          return returnData;
        }
      );
    },
    onError: (error) => {
      console.error("Error updating exception:", error);
    },
  });
};

// export const useUpdateExceptions = (divCode: string): UseMutationResult<Exception, unknown, Partial<Exception>, unknown> => {
//   const queryClient = useQueryClient();
//   return useMutation({
//       mutationFn: updateException,
//       onSuccess: (updatedException: Exception) => {
//           queryClient.setQueryData(['homeSiteExceptions', divCode], (currentExceptions: Exception[] | undefined) =>
//               currentExceptions
//                   ? currentExceptions.map((exception) =>
//                       exception.id === updatedException.id ? updatedException : exception
//                   )
//                   : [updatedException]
//           );
//       },
//       onError: (error) => {
//           console.error('Error updating exception:', error);
//       },
//   });
// }
